// src/components/panel/DailySalesCard.js
import React, { useState, useEffect, useCallback } from 'react';
import { RiseOutlined, FallOutlined, ShoppingCartOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { Spin, Progress, Tooltip } from 'antd';
import analyticsV2Service from '../../services/analyticsV2Service';

function DailySalesCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await analyticsV2Service.getDailySalesProgress();
            
            if (response.success && response.data) {
                setData(response.data);
                setError(null);
            } else {
                setError('Error al cargar datos');
            }
        } catch (err) {
            console.error('Error loading daily sales progress:', err);
            setError('Error al cargar estadísticas de ventas');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
        // Auto-refresh every 60 seconds
        const interval = setInterval(loadData, 60000);
        return () => clearInterval(interval);
    }, [loadData]);

    if (loading) {
        return (
            <div className='card'>
                <div className='card-content-center'>
                    <Spin tip="Cargando ventas del día..." />
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className='card'>
                <div className='card-content-center' style={{ color: '#ff4d4f' }}>
                    {error || 'No se pudieron cargar las estadísticas'}
                </div>
            </div>
        );
    }

    const formatCurrency = (value) => {
        return value?.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00';
    };

    const current = data.current || {};
    const comparison = data.comparison || {};
    const goal = data.goal || {};
    const projection = data.projection || {};
    const statistics = data.statistics || {};

    const isAheadOfAverage = comparison.isAheadOfAverage;
    const percentDiff = comparison.percentDiffVsAverage || 0;
    const timeStr = `${String(current.hour || 0).padStart(2, '0')}:${String(current.minute || 0).padStart(2, '0')}`;

    // Determine performance status color
    const getPerformanceColor = () => {
        if (statistics.performanceCategory === 'exceptional') return '#52c41a'; // Green
        if (statistics.performanceCategory === 'below_average') return '#faad14'; // Orange
        return '#1890ff'; // Blue (normal)
    };

    const getPerformanceIcon = () => {
        if (statistics.performanceCategory === 'exceptional') return '🌟';
        if (statistics.performanceCategory === 'below_average') return '⚠️';
        return '✅';
    };

    const progressPercent = Math.min(goal.progressPercent || 0, 100);

    return (
        <div className='card'>
            <div className='card-inner'>
                <ShoppingCartOutlined className='card_icon' style={{ fontSize: '38px' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <h2>VENTAS HOY</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ 
                            padding: '4px 10px',
                            background: `${getPerformanceColor()}15`,
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: getPerformanceColor(),
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            {getPerformanceIcon()}
                            {statistics.performanceCategory === 'exceptional' && 'Excepcional'}
                            {statistics.performanceCategory === 'normal' && 'Normal'}
                            {statistics.performanceCategory === 'below_average' && 'Bajo Promedio'}
                        </div>
                        <Tooltip title="Actualizado automáticamente cada minuto">
                            <span style={{ fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <ClockCircleOutlined /> {timeStr}
                            </span>
                        </Tooltip>
                    </div>
                </div>
            </div>
            <br />
            <div className='dashboard-card-content'>
                {/* Main Sales Value */}
                <div className='sales-main-value'>
                    <h1>${formatCurrency(current.sales)}</h1>
                    <p className='sales-subtitle'>{current.invoiceCount || 0} facturas</p>
                </div>

                {/* Progress Bar */}
                <div style={{ margin: '16px 0' }}>
                    <Progress 
                        percent={progressPercent} 
                        strokeColor={{
                            '0%': progressPercent < 50 ? '#ff4d4f' : progressPercent < 80 ? '#faad14' : '#52c41a',
                            '100%': progressPercent < 50 ? '#ff7875' : progressPercent < 80 ? '#ffc53d' : '#73d13d'
                        }}
                        showInfo={false}
                        style={{ marginBottom: '8px' }}
                    />
                    <p style={{ fontSize: '12px', color: '#666', textAlign: 'center', margin: 0 }}>
                        {progressPercent.toFixed(1)}% del promedio diario (${formatCurrency(goal.targetSales)})
                    </p>
                    <p style={{ fontSize: '11px', color: '#999', textAlign: 'center', margin: '4px 0 0 0', fontStyle: 'italic' }}>
                        Basado en el promedio de los últimos 7 días
                    </p>
                </div>

                {/* 7-Day Average Comparison */}
                <div className='sales-comparison'>
                    <div className='comparison-row'>
                        <span>Promedio 7 días (a esta hora):</span>
                        <span className='comparison-value'>
                            ${formatCurrency(comparison.sevenDayAverage?.sales)}
                        </span>
                    </div>
                    <div className={`comparison-row ${isAheadOfAverage ? 'positive' : 'warning'}`}>
                        {isAheadOfAverage ? <RiseOutlined /> : <FallOutlined />}
                        <span>{percentDiff > 0 ? '+' : ''}{percentDiff.toFixed(1)}%</span>
                        <Tooltip title={isAheadOfAverage ? 'Por encima del promedio' : 'Por debajo del promedio'}>
                            <span style={{ fontSize: '18px', marginLeft: '8px' }}>
                                {isAheadOfAverage ? '📈' : '📊'}
                            </span>
                        </Tooltip>
                    </div>
                </div>

                {/* Projection */}
                {projection.estimatedEndOfDayTotal > 0 && (
                    <div style={{ 
                        marginTop: '12px', 
                        padding: '12px', 
                        background: '#f9fafb', 
                        borderRadius: '8px'
                    }}>
                        <div className='comparison-row' style={{ marginBottom: '4px' }}>
                            <span>Proyección del día:</span>
                            <span className='comparison-value'>
                                ${formatCurrency(projection.estimatedEndOfDayTotal)}
                            </span>
                        </div>
                        {projection.willBeatGoal ? (
                            <div style={{ color: '#52c41a', fontSize: '12px', marginTop: '4px' }}>
                                💡 +${formatCurrency(projection.differenceVsGoal)} sobre promedio
                            </div>
                        ) : (
                            <div style={{ color: '#faad14', fontSize: '12px', marginTop: '4px' }}>
                                ⏰ {projection.hoursRemaining.toFixed(1)}h restantes • ${formatCurrency(Math.abs(projection.differenceVsGoal))} debajo del promedio
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default DailySalesCard;
